import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ThemeToggle from '../common/ThemeToggle';
import Logo from '../common/Logo';
import api from '../../services/api';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Home', icon: '🏠' },
  { path: '/tree', label: 'Family Tree', icon: '🌳' },
  { path: '/feed', label: 'Feed', icon: '📰' },
  { path: '/milestones', label: 'Milestones', icon: '✨' },
  { path: '/memorial', label: 'Memorials', icon: '🕯️' },
  { path: '/legacy', label: 'Living Legacy', icon: '🏆' },
  { path: '/family', label: 'Family', icon: '👥' },
  { path: '/profile', label: 'Profile', icon: '👤' },
  { path: '/founding-circle', label: 'Founding Circle', icon: '🌱' },
  { path: '/referrals', label: 'Refer & Earn', icon: '🎁' },
  { path: '/payments', label: 'Subscription', icon: '💎' },
  { path: '/support', label: 'Help & Support', icon: '🛟' },
];

const NOTIF_ICONS = {
  invite_received: '📨',
  invite_accepted: '🎉',
  cross_tree_request: '🔗',
  cross_tree_accepted: '✅',
  member_added: '👤',
  join_request: '🚪',
  join_request_approved: '✅',
};

const Sidebar = ({ isOpen, onToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);

  const skipNextFetch = React.useRef(false);

  const fetchNotifications = useCallback(() => {
    if (skipNextFetch.current) { skipNextFetch.current = false; return; }
    api.getNotifications().then(res => {
      setNotifications(res.notifications || []);
      setUnreadCount(res.unreadCount || 0);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Refresh on route change
  useEffect(() => { fetchNotifications(); }, [location, fetchNotifications]);

  const handleMarkAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    skipNextFetch.current = true;
    await api.markAllNotificationsRead().catch(() => {});
    fetchNotifications();
  };

  const handleNotifClick = async (notif) => {
    setNotifications(prev => prev.map(n => n._id === notif._id ? { ...n, read: true } : n));
    if (!notif.read) setUnreadCount(prev => Math.max(0, prev - 1));
    skipNextFetch.current = true;
    await api.markNotificationRead(notif._id).catch(() => {});
    setShowNotifs(false);
    let dest = '/family';
    let state = { _t: Date.now() }; // unique key to force re-trigger on same route
    if (notif.type === 'cross_tree_request' || notif.type === 'cross_tree_accepted') {
      state.scrollTo = 'cross-tree-requests';
      state.linkId = notif.data?.linkId;
    } else if (notif.type === 'invite_received') {
      state.scrollTo = 'members';
      state.tab = 'pending';
    } else if (notif.type === 'invite_accepted' || notif.type === 'member_added') {
      state.scrollTo = 'members';
      state.personId = notif.data?.personId;
    }
    if (notif.data?.treeId) state.treeId = notif.data.treeId;
    navigate(dest, { state, replace: location.pathname === dest });
  };

  const timeAgo = (date) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleLogout = async () => { await logout(); navigate('/'); };
  const initials = user?.displayName ? user.displayName.split(' ').map(n => n[0]).join('').slice(0, 2) : '?';

  // Close sidebar on nav click (mobile)
  const handleNav = () => { if (window.innerWidth < 768) onToggle(); };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && <div onClick={onToggle} style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 45,
        display: window.innerWidth >= 768 ? 'none' : 'block',
      }} />}

      {/* Notification dropdown overlay */}
      {showNotifs && <div onClick={() => setShowNotifs(false)} style={{ position: 'fixed', inset: 0, zIndex: 55 }} />}

      <aside style={{
        width: 240, minHeight: '100vh',
        background: 'var(--bg-sidebar)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column',
        position: 'fixed', left: isOpen ? 0 : -240, top: 0, bottom: 0, zIndex: 50,
        transition: 'left 0.3s ease',
      }}>
        <div style={{
          padding: '20px 20px 16px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div onClick={() => { navigate('/dashboard'); handleNav(); }} style={{ cursor: 'pointer' }}>
            <Logo size={32} />
          </div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>LegacyRoots</span>

          {/* Notification bell */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => {
              const opening = !showNotifs;
              setShowNotifs(opening);
              if (opening && unreadCount > 0) handleMarkAllRead();
            }} style={{
              width: 32, height: 32, borderRadius: 10, background: showNotifs ? 'var(--accent-green-light)' : 'var(--bg-secondary)',
              border: '1px solid var(--border)', color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 16, position: 'relative',
            }}>
              🔔
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: -4, right: -4, minWidth: 18, height: 18,
                  borderRadius: 9, background: '#E74C3C', color: '#FFF',
                  fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 4px', border: '2px solid var(--bg-sidebar)',
                }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
              )}
            </button>

            {/* Notification dropdown */}
            {showNotifs && (
              <div style={{
                position: 'absolute', top: 40, left: 0, width: 320,
                maxHeight: 420, overflowY: 'auto',
                borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--border)',
                boxShadow: 'var(--shadow-lg)', zIndex: 60,
              }}>
                <div style={{
                  padding: '14px 16px 10px', borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 15, fontWeight: 700, fontFamily: "'Cormorant Garamond',serif", color: 'var(--text-primary)' }}>Notifications</span>
                  {unreadCount > 0 && (
                    <button onClick={handleMarkAllRead} style={{
                      background: 'none', border: 'none', color: 'var(--accent-green)',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    }}>Mark all read</button>
                  )}
                </div>

                {notifications.length === 0 ? (
                  <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>
                    No notifications yet
                  </div>
                ) : (
                  notifications.map(notif => (
                    <div
                      key={notif._id}
                      onClick={() => handleNotifClick(notif)}
                      style={{
                        padding: '12px 16px', cursor: 'pointer',
                        borderBottom: '1px solid var(--border)',
                        background: notif.read ? 'transparent' : 'var(--accent-green-light)',
                        transition: 'background 0.2s',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 18, flexShrink: 0, marginTop: 2 }}>{NOTIF_ICONS[notif.type] || '🔔'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: notif.read ? 400 : 600, color: 'var(--text-primary)', marginBottom: 2 }}>
                            {notif.title}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                            {notif.message}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                            {timeAgo(notif.createdAt)}
                          </div>
                        </div>
                        {!notif.read && (
                          <span style={{
                            width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-green)',
                            flexShrink: 0, marginTop: 6,
                          }} />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Close button on mobile */}
          <button onClick={onToggle} style={{
            display: window.innerWidth < 768 ? 'flex' : 'none',
            width: 28, height: 28, borderRadius: 8, background: 'var(--bg-secondary)',
            border: '1px solid var(--border)', color: 'var(--text-muted)',
            alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14,
          }}>×</button>
        </div>

        <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
          {NAV_ITEMS.map(item => (
            <NavLink key={item.path} to={item.path} onClick={handleNav}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px', borderRadius: 12, marginBottom: 2,
                textDecoration: 'none',
                background: isActive ? 'var(--accent-green-light)' : 'transparent',
                color: isActive ? 'var(--accent-green)' : 'var(--text-muted)',
                fontSize: 14, fontWeight: isActive ? 600 : 400,
                transition: 'all 0.2s',
              })}
            >
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: '14px 16px', borderTop: '1px solid var(--border)' }}>
          <div onClick={() => { navigate('/profile'); handleNav(); }} style={{
            display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
            cursor: 'pointer', padding: '6px 8px', borderRadius: 10,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--accent-green), #7BAF6E)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#FFF', fontFamily: 'var(--font-display)',
              flexShrink: 0,
            }}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.displayName || 'User'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{user?.subscription?.plan || 'roots'} plan</div>
            </div>
          </div>
          <button onClick={handleLogout} style={{
            width: '100%', padding: 8, borderRadius: 10,
            background: 'var(--bg-primary)', border: '1px solid var(--border)',
            color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
          }}>Sign Out</button>
          <div
            onClick={() => { navigate('/delete-account'); handleNav(); }}
            style={{ textAlign: 'center', marginTop: 8, fontSize: 11, color: 'var(--text-dim)', cursor: 'pointer' }}
          >Delete Account</div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
